/**
 * TECDoc Sprite Component
 * 
 * A React wrapper for the TECDoc sprite system that provides a clean API
 * while maintaining compatibility with the existing sprite CSS classes.
 * 
 * Usage:
 *   <TecSprite icon="building" size="sm" color="black" />
 *   <TecSprite icon="stethoscope" size="md" color="red" />
 *   <TecSprite icon="gear" size="lg" color="white" />
 */

import React from 'react';

export type SpriteColor = 'black' | 'white' | 'red';
export type SpriteSize = 'sm' | 'md' | 'lg' | 'standin';
export type SpriteIcon = 
  | 'building'
  | 'stethoscope' 
  | 'gear'
  | 'clipboard'
  | 'bandaid'
  | 'people'
  | 'graph'
  | 'pencil'
  | 'megaphone'
  | 'mail'
  | 'star'
  | 'plus';

interface TecSpriteProps {
  /** The sprite icon name */
  icon: SpriteIcon;
  /** The sprite color variant */
  color: SpriteColor;
  /** The sprite size */
  size?: SpriteSize;
  /** Additional CSS classes */
  className?: string;
  /** Click handler */
  onClick?: () => void;
  /** Title for accessibility */
  title?: string;
  /** Style overrides */
  style?: React.CSSProperties;
}

/**
 * TECDoc Sprite component that uses the existing CSS sprite system
 */
export const TecSprite: React.FC<TecSpriteProps> = ({
  icon,
  color,
  size = 'sm',
  className = '',
  onClick,
  title,
  style
}) => {
  // Build the sprite class names following TECDoc convention
  const spriteClasses = [
    `tec-sprite-${color}`,
    `tec-sprite-${icon}${size ? `-${size}` : ''}`,
    className
  ].filter(Boolean).join(' ');

  // Set dimensions based on size to ensure proper sprite display
  const getDimensions = () => {
    switch (size) {
      case 'sm': return { width: '20px', height: '20px' };
      case 'md': return { width: '30px', height: '30px' };
      case 'lg': return { width: '39px', height: '39px' };
      case 'standin': return { width: '39px', height: '39px' };
      default: return { width: '20px', height: '20px' };
    }
  };
  
  const spriteDimensions = getDimensions();

  return (
    <div
      className={spriteClasses}
      onClick={onClick}
      title={title}
      style={{
        cursor: onClick ? 'pointer' : undefined,
        ...spriteDimensions,
        ...style
      }}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
    />
  );
};

/**
 * Utility component for common sprite patterns
 */
export const UserAvatarSprite: React.FC<{ className?: string }> = ({ className }) => (
  <TecSprite
    icon="building"
    color="black"
    size="sm"
    className={className}
    title="User Avatar"
  />
);

/**
 * Higher-order component to wrap sprites in containers (like tec-standin)
 */
export const TecStandin: React.FC<{ 
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <div className={`tec-standin ${className}`}>
    {children}
  </div>
);

export default TecSprite;